<template>
  <v-card>
    <v-card-title class="report-title">
      <v-col cols="12">
        <h4>{{ `${$t('report.time_period')}: ${reportData.startDate} - ${reportData.endDate}` }}</h4>
      </v-col>
    </v-card-title>
    <v-card-text>
      <v-card v-for="(item, key) in reportItems" :key="key" :elevation="key === 0 ? 4 : 1">
        <v-container pa-3 :mb-2="key !== 0" :mb-4="key === 0">
          <v-layout
            row
            wrap
            :class="[{'topic-header-with-value': item.topic.value !== undefined}, item.topic.type]"
          >
            <v-col cols="10" class="py-1">
              <h3 class="topic-header primary--text">{{ item.topic.header }}</h3>
              <span class="hint">{{ item.topic.hint }}</span>
            </v-col>
            <v-col cols="2" class="py-1">
              <span>{{ item.topic.value }}</span>
            </v-col>
          </v-layout>

          <v-layout
            row
            wrap
            v-for="(entry,key) in item.entries"
            @click="showDetails(entry.details)"
            :class="['report-entry', entry.type]"
            :key="key"
          >
            <template v-if="typeof entry.show === 'undefined' || entry.show">
              <v-col cols="10" class="py-1">
                <span>{{ entry.header }}</span>
                <span class="hint">{{ entry.hint }}</span>
              </v-col>
              <v-col cols="2" class="py-1">
                <span>{{ entry.value || '-' }}</span>
              </v-col>
            </template>
          </v-layout>
        </v-container>
      </v-card>
      <v-flex class="hint report-remarks">{{ $t('report.remarks') }}</v-flex>
    </v-card-text>
    <v-dialog v-model="isShowDetails" max-width="650">
      <sales-summary :sales-summary="salesSummary" :details-loading="detailsLoading"></sales-summary>
    </v-dialog>
  </v-card>
</template>

<script>
import _ from 'lodash'

import locationService from '@/services/location'

import SalesSummary from '@/components/location/SalesSummary'

export default {
  components: {
    SalesSummary
  },

  props: {
    reportData: Object,
    lastMonth: String
  },

  data () {
    return {
      isShowDetails: false,
      salesSummary: [],
      detailsLoading: false
    }
  },

  computed: {
    reportItems () {
      let balance = this.reportData.data['Available balance at period end']
      let cost = this.reportData.data['Cost']
      let proceeds = this.reportData.data['Franchise total proceeds']
      let profitShare = this.reportData.data['Franchisee profit share']
      let profit = this.reportData.data['Machine profit']
      let otherRevenue = this.reportData.data['Other Revenue']
      let sales = this.reportData.data['Total sales']
      let others = this.reportData.data['other']

      let totalCostAndExpense = _.round(+cost['Cost of goods sold'] + +cost['Rent'] + +cost['Octopus (5% of sales)'] + +cost['Internet'], 2)
      let withdrawal = _.round(+proceeds['Franchise total proceeds'] + +balance['Available balance brought forward'] - +balance['Available balance at period end'], 2)
      let totalRevenue = _.round(totalCostAndExpense + profit['Machine profit'], 2)

      return [
        // Account Balance
        {
          topic: {
            header: this.$t('report.account_balance'),
            value: balance['Available balance at period end'],
            type: 'subtotal'
          },
          entries: [
            {
              header: this.$t('report.total_proceeds_with_period', { period: this.reportData.periodName }),
              value: proceeds['Franchise total proceeds']
            },
            {
              header: this.$t('report.balance_brought_forward', { period: this.lastMonth || '/' }),
              value: balance['Available balance brought forward']
            },
            {
              header: this.$t('report.withdrawal', { period: this.reportData.periodName || '/' }),
              value: `-${withdrawal}`,
              show: withdrawal > 0,
              type: 'subtotal'
            }
          ]
        },
        // Revenue
        {
          topic: {
            header: this.$t('report.revenue')
          },
          entries: [
            {
              header: this.$t('report.merchandise_sales'),
              value: sales['Total sales'],
              details: 'sales-summary',
              type: 'clickable'
            },
            {
              header: this.$t('report.marketing_service'),
              value: others['Marketing service'],
              show: !_.isEmpty(others)
            },
            {
              header: this.$t('report.sales_channel_rental_service'),
              value: others['Sales channel rental service'],
              show: !_.isEmpty(others)
            },
            {
              header: this.$t('report.o2o_service'),
              value: others['O2O service'],
              show: !_.isEmpty(others) && others['O2O service'] !== null
            },
            {
              header: this.$t('report.other_revenue'),
              value: otherRevenue['Total other revenue'],
              show: _.isEmpty(others)
            },
            {
              header: `${this.$t('report.total_revenue')} (A)`,
              value: totalRevenue,
              type: 'subtotal'
            }
          ]
        },
        // Cost & Expenses
        {
          topic: {
            header: this.$t('report.cost_and_expenses')
          },
          entries: [
            {
              header: this.$t('report.cost_of_goods_sold'),
              value: cost['Cost of goods sold']
            },
            {
              header: this.$t('report.rent'),
              hint: cost['Rent Clause'],
              value: cost['Rent']
            },
            {
              header: this.$t('report.payment_fee'),
              hint: this.$t('report.payment_fee_hint', { rate: '1.6% - 5' }),
              value: cost['Octopus (5% of sales)']
            },
            {
              header: this.$t('report.internet'),
              value: cost['Internet']
            },
            {
              header: `${this.$t('report.total_cost_and_expenses')} (B)`,
              value: totalCostAndExpense,
              type: 'subtotal'
            }
          ]
        },
        // Net Profit
        {
          topic: {
            header: `${this.$t('report.net_profit')} (A - B)`,
            value: profit['Machine profit'],
            type: 'subtotal'
          }
        },
        // Profit Share
        {
          topic: {
            header: this.$t('report.profit_share')
          },
          entries: [
            {
              header: 'Gritus',
              hint: this.$t('report.profit_share_hint', { rate: profitShare['Gritus share %'] }),
              value: profitShare['Gritus Profit share'],
              type: 'subtotal'
            },
            {
              header: `${this.$t('report.franchisee')} (C)`,
              hint: this.$t('report.profit_share_hint', { rate: (100 - profitShare['Gritus share %']) || '' }),
              value: profitShare['Franchisee profit share'],
              type: 'subtotal'
            }
          ]
        },
        // Guaranteed Clause
        {
          topic: {
            header: this.$t('report.guaranteed_clause')
          },
          entries: [
            {
              header: `${this.$t('report.guaranteed_clause_proceed')} (D)`,
              value: proceeds['Proceed from guaranteed clause'],
              type: 'subtotal'
            }
          ]
        },
        // Management Fee
        {
          topic: {
            header: this.$t('report.management_fee')
          },
          entries: [
            {
              header: `Gritus ${this.$t('report.management_fee')} (E)`,
              hint: this.$t('report.gritus_management_fee_hint', { amount: '1000' }),
              value: proceeds['Gritus management fee'],
              type: 'subtotal'
            }
          ]
        },
        // Total Proceeds
        {
          topic: {
            header: `${this.$t('report.total_proceeds')} (C + D - E)`
          },
          entries: [
            {
              header: this.$t('report.total_proceeds_with_period', { period: this.reportData.periodName }),
              value: proceeds['Franchise total proceeds'],
              type: 'subtotal'
            }
          ]
        }
      ]
    }
  },

  created () {
    this.loadDetails()
  },

  methods: {
    async loadDetails () {
      this.detailsLoading = true
      try {
        let { data } = await locationService.getDetails(this.reportData.machineName, this.reportData.periodID)
        this.salesSummary = data.data.sales_summary
      } finally {
        this.detailsLoading = false
      }
    },

    showDetails (details) {
      if (typeof details === 'undefined') {
        return
      }

      this.isShowDetails = true
    }
  }
}
</script>

<style lang="stylus" scoped>
.report-title
  padding-bottom: 0px

.topic-header
  color: var(--v-primary-base)
  display: inline

.hint
  margin-left: 10px

.report-entry:hover, .topic-header-with-value:hover
  background-color: LightGrey

.report-remarks
  margin-top: 20px

.subtotal
  font-weight: bold

.clickable
  cursor: pointer
  text-decoration: underline
  color: var(--v-primary-base)
</style>
